import * as XLSX from 'xlsx';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse, BvSiteNetwork, BvSiteNetworkFormData, BvTariffUsage, BvTariffPeriod } from '../types/billValidationTypes';

export type NetworkBulkUploadResult = {
  success: boolean;
  total: number;
  inserted: number;
  errors: Array<{ row: number; error: string }>;
  error?: string;
};

/** Empty → null. Invalid JSON or a non-object → throw, so save fails visibly.
 * A silently-dropped threshold prices a site wrong with no error (design §4.5). */
export const parseTreatmentParams = (raw: string): Record<string, unknown> | null => {
  const text = (raw ?? '').trim();
  if (!text) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Treatment params must be valid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Treatment params must be a JSON object, e.g. {"threshold_kw": 30}');
  }
  return parsed as Record<string, unknown>;
};

// Same physical distributor, different names across data sources
// (AEMO NSP registration name vs current public trading name)
const DISTRIBUTOR_ALIASES: string[][] = [
  ['evoenergy', 'icon distribution/jemena', 'icon water', 'actewagl'],
];

function normaliseDistributorName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+(networks|distribution)$/i, '');
}

/** True if two distributor names refer to the same network distributor */
export function distributorsMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const na = normaliseDistributorName(a);
  const nb = normaliseDistributorName(b);
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  return DISTRIBUTOR_ALIASES.some(group => group.includes(na) && group.includes(nb));
}

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

export const siteNetworkService = {
  /** All tariff codes defined for this org */
  listAll: async (): Promise<ApiResponse<BvSiteNetwork[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      // Since migration 060 this table holds one row per (org, distributor,
      // tariff_code, bucket), so an org runs into thousands of rows. An unbounded
      // select is capped at 1000 by Supabase, and because the order is by
      // tariff_code the truncation fell on codes late in the alphabet — a site
      // assigned one of those read as "No network tariffs assigned to this site"
      // (job 1). Page through instead, ordered so paging is stable.
      const all: BvSiteNetwork[] = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('bv_site_network')
          .select('*')
          .eq('org_id', orgId)
          .order('tariff_code', { ascending: true })
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as BvSiteNetwork[]));
        if (!data?.length || data.length < PAGE_SIZE) break;
        page++;
      }
      return { success: true, data: all };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Fetch network records for a specific list of tariff codes */
  getByCodes: async (codes: string[]): Promise<ApiResponse<BvSiteNetwork[]>> => {
    if (!codes.length) return { success: true, data: [] };
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      // Bounded by the code list, but each code is one row per bucket — enough
      // codes and this hits the same 1000-row cap as listAll. A silently
      // truncated rate set would misprice the expected invoice, so page it.
      const all: BvSiteNetwork[] = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('bv_site_network')
          .select('*')
          .eq('org_id', orgId)
          .in('tariff_code', codes)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as BvSiteNetwork[]));
        if (!data?.length || data.length < PAGE_SIZE) break;
        page++;
      }
      return { success: true, data: all };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Create a new tariff code in the lookup table */
  create: async (form: BvSiteNetworkFormData): Promise<ApiResponse<BvSiteNetwork>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const toNum = (v: string) => v.trim() === '' ? null : Number(v);
      const { data, error } = await supabase
        .from('bv_site_network')
        .insert({
          org_id: orgId,
          tariff_code: form.tariff_code.trim().toUpperCase(),
          tariff_name: form.tariff_name.trim() || null,
          distributor: form.distributor.trim() || null,
          description: form.description.trim() || null,
          name: form.name.trim() || null,
          channel: form.channel.trim() || null,
          market_region: form.market_region.trim() || null,
          days_mapping_id:   form.days_mapping_id   || null,
          times_mapping_id:  form.times_mapping_id  || null,
          months_mapping_id: form.months_mapping_id || null,
          time_type_id:      form.time_type_id      || null,
          treatment_id:      form.treatment_id      || null,
          bucket_key:        form.bucket_key        || null,
          bill_category:     form.bill_category     || null,
          demand_metric:     form.demand_metric     || null,
          interval_min:      form.interval_min ? Number(form.interval_min) : null,
          notes:             form.notes             || null,
          event_type:        form.event_type        || null,
          treatment_params:  parseTreatmentParams(form.treatment_params),
          category: form.category.trim() || null,
          open_to_new_applicants: form.open_to_new_applicants ?? true,
          price: toNum(form.price),
          price_type: form.price_type.trim() || null,
        })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as BvSiteNetwork };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Update an existing tariff code record */
  update: async (id: string, form: Partial<BvSiteNetworkFormData>): Promise<ApiResponse<BvSiteNetwork>> => {
    try {
      const toNum = (v: string | undefined) => v === undefined || v.trim() === '' ? null : Number(v);
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (form.tariff_code !== undefined) patch.tariff_code = form.tariff_code.trim().toUpperCase();
      if (form.tariff_name !== undefined) patch.tariff_name = form.tariff_name.trim() || null;
      if (form.distributor !== undefined) patch.distributor = form.distributor.trim() || null;
      if (form.description !== undefined) patch.description = form.description.trim() || null;
      if (form.name !== undefined) patch.name = form.name.trim() || null;
      if (form.channel !== undefined) patch.channel = form.channel.trim() || null;
      if (form.market_region !== undefined) patch.market_region = form.market_region.trim() || null;
      if (form.days_mapping_id !== undefined)   patch.days_mapping_id   = form.days_mapping_id   || null;
      if (form.times_mapping_id !== undefined)  patch.times_mapping_id  = form.times_mapping_id  || null;
      if (form.months_mapping_id !== undefined) patch.months_mapping_id = form.months_mapping_id || null;
      if (form.time_type_id !== undefined)      patch.time_type_id      = form.time_type_id      || null;
      if (form.treatment_id !== undefined)      patch.treatment_id      = form.treatment_id      || null;
      if (form.bucket_key !== undefined)        patch.bucket_key        = form.bucket_key        || null;
      if (form.bill_category !== undefined)     patch.bill_category     = form.bill_category     || null;
      if (form.demand_metric !== undefined)     patch.demand_metric     = form.demand_metric     || null;
      if (form.interval_min !== undefined)      patch.interval_min      = form.interval_min ? Number(form.interval_min) : null;
      if (form.notes !== undefined)             patch.notes             = form.notes             || null;
      if (form.event_type !== undefined)        patch.event_type        = form.event_type        || null;
      if (form.treatment_params !== undefined)  patch.treatment_params  = parseTreatmentParams(form.treatment_params);
      if (form.category !== undefined) patch.category = form.category.trim() || null;
      if (form.open_to_new_applicants !== undefined) patch.open_to_new_applicants = form.open_to_new_applicants;
      if ('price' in form) patch.price = toNum(form.price);
      if (form.price_type !== undefined) patch.price_type = form.price_type.trim() || null;
      const { data, error } = await supabase
        .from('bv_site_network')
        .update(patch)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as BvSiteNetwork };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Delete a tariff code from the lookup table */
  delete: async (id: string): Promise<ApiResponse<void>> => {
    try {
      const { error } = await supabase.from('bv_site_network').delete().eq('id', id);
      if (error) throw error;
      return { success: true, data: undefined };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Persist the site's selected network_codes array */
  updateSiteCodes: async (siteId: string, codes: string[]): Promise<ApiResponse<void>> => {
    try {
      const { error } = await supabase
        .from('site')
        .update({ network_codes: codes })
        .eq('id', siteId);
      if (error) throw error;
      return { success: true, data: undefined };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Parse a CSV/XLSX file and insert each row as a new tariff code for this org */
  bulkUpload: async (file: File): Promise<NetworkBulkUploadResult> => {
    let rawRows: Record<string, unknown>[];
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array', raw: false, dateNF: 'yyyy-mm-dd' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
    } catch {
      return { success: false, total: 0, inserted: 0, errors: [], error: 'Failed to parse file — ensure valid CSV or XLSX' };
    }

    if (rawRows.length === 0) return { success: false, total: 0, inserted: 0, errors: [], error: 'File has no data rows' };
    if (rawRows.length > 2000) return { success: false, total: 0, inserted: 0, errors: [], error: 'File exceeds 2,000 row limit' };

    const orgId = await getOrgId();
    if (!orgId) return { success: false, total: rawRows.length, inserted: 0, errors: [], error: 'Organization not found' };

    const toNum = (v: unknown) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const errors: Array<{ row: number; error: string }> = [];
    let inserted = 0;

    // Bulk upload speaks vocabulary *codes* ('WORKING_DAYS'), not UUIDs — a
    // spreadsheet round-trip must stay human-editable.
    const [days, times, months, timeTypes, treatments] = await Promise.all([
      supabase.from('bv_days_mapping').select('id, code'),
      supabase.from('bv_times_mapping').select('id, code'),
      supabase.from('bv_months_mapping').select('id, code'),
      supabase.from('bv_time_type').select('id, code'),
      supabase.from('bv_tariff_treatment').select('id, code'),
    ]);
    const idByCode = (res: { data: Array<{ id: string; code: string }> | null }) =>
      new Map((res.data ?? []).map(r => [r.code.toUpperCase(), r.id]));
    const daysIds = idByCode(days);
    const timesIds = idByCode(times);
    const monthsIds = idByCode(months);
    const timeTypeIds = idByCode(timeTypes);
    const treatmentIds = idByCode(treatments);

    for (let i = 0; i < rawRows.length; i++) {
      const rowNum = i + 2;
      const norm: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(rawRows[i])) {
        norm[k.toLowerCase().trim().replace(/\s+/g, '_')] = v;
      }

      const tariff_code = String(norm.tariff_code ?? '').trim().toUpperCase();
      if (!tariff_code) { errors.push({ row: rowNum, error: 'Missing tariff_code' }); continue; }

      const lookup = (m: Map<string, string>, raw: unknown): string | null =>
        raw ? (m.get(String(raw).trim().toUpperCase()) ?? null) : null;

      const parseBool = (v: unknown, def = true): boolean => {
        const s = String(v ?? '').toLowerCase().trim();
        if (s === '') return def;
        return s === 'true' || s === '1' || s === 'yes';
      };

      const days_mapping_id   = lookup(daysIds,      norm.days_code);
      const times_mapping_id   = lookup(timesIds,     norm.times_code);
      const months_mapping_id  = lookup(monthsIds,    norm.months_code);
      const time_type_id       = lookup(timeTypeIds,  norm.time_type_code);
      const treatment_id       = lookup(treatmentIds, norm.treatment_code);

      // Unknown code → leave unmapped (recoverable in the UI), but warn so it's visible.
      if (norm.days_code && !days_mapping_id) {
        errors.push({ row: rowNum, error: `Unknown days code "${String(norm.days_code)}" — left unmapped` });
      }
      if (norm.times_code && !times_mapping_id) {
        errors.push({ row: rowNum, error: `Unknown times code "${String(norm.times_code)}" — left unmapped` });
      }
      if (norm.months_code && !months_mapping_id) {
        errors.push({ row: rowNum, error: `Unknown months code "${String(norm.months_code)}" — left unmapped` });
      }
      if (norm.time_type_code && !time_type_id) {
        errors.push({ row: rowNum, error: `Unknown time_type code "${String(norm.time_type_code)}" — left unmapped` });
      }
      if (norm.treatment_code && !treatment_id) {
        errors.push({ row: rowNum, error: `Unknown treatment code "${String(norm.treatment_code)}" — left unmapped` });
      }

      const { error: dbErr } = await supabase.from('bv_site_network').insert({
        org_id: orgId,
        tariff_code,
        tariff_name: String(norm.tariff_name ?? '').trim() || null,
        distributor: String(norm.distributor ?? '').trim() || null,
        description: String(norm.description ?? '').trim() || null,
        name: String(norm.name ?? norm.time_type ?? '').trim() || null,
        channel: String(norm.channel ?? '').trim() || null,
        market_region: String(norm.market_region ?? '').trim() || null,
        days_mapping_id,
        times_mapping_id,
        months_mapping_id,
        time_type_id,
        treatment_id,
        category: String(norm.category ?? '').trim() || null,
        open_to_new_applicants: parseBool(norm.open_to_new_applicants ?? norm.tariff_open_to_new_applicants),
        price: toNum(norm.price),
        price_type: String(norm.price_type ?? '').trim() || null,
      });

      if (dbErr) errors.push({ row: rowNum, error: dbErr.message });
      else inserted++;
    }

    return { success: true, total: rawRows.length, inserted, errors };
  },

  /** Persist network_tariff_usage JSON for a site */
  updateTariffUsage: async (siteId: string, usage: Record<string, BvTariffUsage>): Promise<ApiResponse<void>> => {
    try {
      const { error } = await supabase
        .from('site')
        .update({ network_tariff_usage: usage })
        .eq('id', siteId);
      if (error) throw error;
      return { success: true, data: undefined };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  /** Persist network_tariff_dates JSON for a site (migration 099) — when each
   *  assigned code applied. Same shape and same save path as the usage map
   *  above; they are two facts about the same key. */
  updateTariffDates: async (
    siteId: string, dates: Record<string, BvTariffPeriod>,
  ): Promise<ApiResponse<void>> => {
    try {
      const { error } = await supabase
        .from('site')
        .update({ network_tariff_dates: dates })
        .eq('id', siteId);
      if (error) throw error;
      return { success: true, data: undefined };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },
};
